import { useContext, useMemo, useState } from "react";

import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { SimpleTable } from "../../../common/components/functional/simpleTable/SimpleTable";
import useNotification from "../../../hooks/useNotification";
import { PayslipRowHeader } from "../../talentProfile/Employment/PayslipOverview/tableItems/PayslipRowHeader";
import PayslipRowItem from "../../talentProfile/Employment/PayslipOverview/tableItems/PayslipRowItem";
import { PayslipTotalsRow } from "../../talentProfile/Employment/PayslipOverview/tableItems/PayslipTotalsRow";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { canExpertModifyPayslips } from "../../user/accessRightChecks";
import {
  postApprovePayslip,
  postDeletePayslip,
  postLockPayslip,
  postUnapprovePayslip,
} from "./payslipsSlice";
import { PayslipDataRow } from "./rowHelpers";

type Props = {
  rowFields: RowField[];
  rows: PayslipDataRow[];
  addItem?: () => void;
  editRow?: (payslipId: string) => void;
  showTotals?: boolean;
};

export interface RowField {
  name: string;
  width?: string;
}

export const PayslipsTable = ({
  rowFields,
  rows,
  addItem,
  editRow,
  showTotals,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { sendNotification } = useNotification();

  const { canEditPayslips } = useContext(AccessRightContext);
  const { canDeletePayslips } = useContext(AccessRightContext);
  const { totals } = useAppSelector((state) => state.payslips);

  const [confirmRowActionId, setConfirmRowActionId] = useState<string | null>(
    null,
  );

  const lockRow = () => {
    if (!confirmRowActionId) return;
    dispatch(postLockPayslip(confirmRowActionId)).then((action) => {
      setConfirmRowActionId(null);
      if (
        isFulfilled(action) &&
        !action.payload.find((event) => event.name === "pspLoc")
      ) {
        sendNotification(
          t("payslips:payslipLockFailed"),
          t("payslips:payslipWasNotLocked"),
        );
      }
    });
  };

  const rowComponents = useMemo(() => {
    return rows.map((row, index) => (
      <PayslipRowItem
        key={index}
        onApprove={() => dispatch(postApprovePayslip(row.id))}
        onRemoveApproval={() => dispatch(postUnapprovePayslip(row.id))}
        editRow={editRow}
        onDelete={() => dispatch(postDeletePayslip(row.id))}
        isUserAllowedToDelete={row.isManual || canDeletePayslips}
        row={row.fields}
        rowId={row.id}
        isUserAllowedToEdit={canEditPayslips}
        openConfirmationDialog={setConfirmRowActionId}
      />
    ));
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, canEditPayslips, dispatch, editRow]);

  const items = rows && [
    <>{!!showTotals && totals ? <PayslipTotalsRow totals={totals} /> : null}</>,
    ...rowComponents,
  ];

  return (
    <>
      <SimpleTable
        header={<PayslipRowHeader fields={rowFields} />}
        items={items}
        addItem={addItem}
        accessRightChecks={[canExpertModifyPayslips]}
      />
      {!!confirmRowActionId ? (
        <ConfirmationDialog
          title={t("payslips:confirmLock")}
          contentText={t("payslips:confirmLockInfo")}
          cancelText={t("common:cancel")}
          confirmText={t("payslips:lock")}
          isOpen={!!confirmRowActionId}
          onConfirm={lockRow}
          onCancel={() => setConfirmRowActionId(null)}
        />
      ) : null}
    </>
  );
};
